import {DomainStore} from 'shared/store';
import { makeAutoObservable } from 'mobx';
import {t} from 'shared/core';
import {endpoints, types, formatTimeOffAmount} from 'main/tools';
import {calendarDate, Steps} from 'shared/tools';
import {TimeOffPolicy, TimeOffTypePolicy, TimeOffType, BalanceAdjustmentPolicy, TypePolicyConfirmationSummary, AnniversaryBonus} from 'main/stores/time_off';
import {initialSteps, finalSteps} from './steps';
import {User} from 'main/stores/users';
import _ from 'lodash';
import moment from 'moment';

class PolicyEditFlowState {
  store = new DomainStore();
  history;
  match;

  policy;
  oldTypePolicy;
  typePolicy;
  balanceAdjustmentPolicy;
  accrualType = 'standard';
  carryoverType;
  availableAssignees;
  waitPeriodEnabled = false;
  confirmationSummary;
  steps;
  errors = {};

  effectiveDateType;
  forecastingEmployeeId;
  forecastDate;
  forecastedValues;
  isForecastingEmployee = false;
  policyConfirmationModalOpen = false;
  typeRemovalConfirmationModalOpen = false;

  hoursPerWorkday;
  workDayLengthChanged = false;
  startMonthDate;

  timeOffTypes = [];
  selectedTimeOffTypes = [];

  availableEmployees = [];
  selectedEmployees = [];
  welcomeEmailPreview;

  constructor() {
    makeAutoObservable(this, {
      store: false,
      history: false
    });
  }

  get typePolicyProps() {
    let props = [
      'accrualFrequency',
      'baseAccrualRate',
      'accrualStart',
      'accrueUpfront',
      'prorationEnabled',
      'approvalType',
      'anniversaryBonuses',
      'balanceAdjustmentPolicy',
      'secondStageApprovalType',
      'secondStageApprovalAssigneeUser',
      'firstStageApprovers',
      'secondStageApprovers',
      'minimumRequestIncrement',
      'preventNegativeBalance'
    ];
    if (this.waitPeriodEnabled) props.push('waitPeriod');
    if (this.usesAccrualStartDate) props.push('accrualStartDate');
    return props;
  }

  receiveProps({history, match}) {
    this.history = history;
    this.match = match;
  }

  async load() {
    await this.store._compose([
      endpoints.TIME_OFF.POLICY.with(this.match.params.id),
      endpoints.USERS.ACTIVE,
      endpoints.TIME_OFF.TYPES.ALL
    ]);

    this.policy = new TimeOffPolicy(this.store._getSingle(types.TIME_OFF.POLICY));
    this.timeOffTypes = this.store._getAll(types.TIME_OFF.TYPE, TimeOffType);
    this.hoursPerWorkday = this.policy.workDayLength / 3600;
    this.startMonthDate = new Date(new Date().getFullYear(), 6, 1);
    this._initializeSteps();
  }

  async onStepChanged({location, subStepLocation}) {
    switch (location) {
      case 'policy_types':
        await this.initializeTimeOffTypes();
        break;
      case 'employees':
        await this.initializeEmployees();
        break;
      case 'time_off_rules':
        this.initializeTypePolicy(subStepLocation);
        break;
      case 'review':
        await this.initializeEmailTemplate();
        break;
      default:
        break;
    }
  }

  async initializeTimeOffTypes() {
    this.selectedTimeOffTypes = this.store._getAll(
      types.TIME_OFF.TYPE,
      type => _.map(this.policy.typePolicies, 'type.id').includes(type.id),
      TimeOffType
    );

    this.currentlySelectedTimeOffTypes = this.selectedTimeOffTypes.slice()
  }

  async initializeEmployees() {
    this.selectedEmployees = this.policy.employees.slice();
  }

  initializeTypePolicy(typePolicyId) {
    this.typePolicy = new TimeOffTypePolicy(
      this.store._getSingle(types.TIME_OFF.TYPE_POLICY, {id: typePolicyId})
    );
    this.typePolicy.anniversaryBonuses = this.typePolicy.sortedAnniversaryBonuses;
    this.oldTypePolicy = new TimeOffTypePolicy(this.typePolicy);

    this.mapApproversFromBackend(this.typePolicy.firstStageApprovers);
    this.mapApproversFromBackend(this.typePolicy.secondStageApprovers);
    this.initializeAccrualFrequency();
    this.initializeBalanceAdjustmentPolicy();
    this.initializeWaitingPeriod();
    this.initializeAssignees();
  }

  initializeAccrualFrequency() {
    if (this.typePolicy.accrualFrequency === 'unlimited') {
      this.accrualType = 'unlimited';
      this.typePolicy.accrualStartDate = null;
    } else {
      this.accrualType = 'standard';
    }
  }

  initializeWaitingPeriod() {
    this.waitPeriodEnabled = this.typePolicy.waitPeriod > 0;
    this.typePolicy.waitPeriod = this.waitPeriodEnabled ? this.typePolicy.waitPeriod : null;
  }

  initializeBalanceAdjustmentPolicy() {
    if (this.typePolicy.accrualFrequency === 'unlimited') {
      return;
    }

    if (!this.typePolicy.balanceAdjustmentPolicy ) {
      this.typePolicy.balanceAdjustmentPolicy = new BalanceAdjustmentPolicy({type: 'AnnualResetPolicy'});
    }
    const {balanceAdjustmentPolicy} = this.typePolicy;

    switch (balanceAdjustmentPolicy.type) {
      case 'AnnualResetPolicy':
        this.carryoverType = 'annual_reset';
        break;
      case 'NoLimitPolicy':
        this.carryoverType = 'no_limit';
        break;
      case 'BalanceLimitPolicy':
        this.carryoverType = 'balance_limit';
        break;
      case 'CarryoverPolicy':
        this.carryoverType = balanceAdjustmentPolicy.limitDays ? 'capped_with_deadline' : 'capped';
        break;
      default:
        throw new Error(`Balance adjustment type ${balanceAdjustmentPolicy.type} is not supported`);
    }
  }

  initializeAssignees() {
    const users = this.store._getAll(types.USER).map(u => new User(u));
    this.availableAssignees = [
      {
        userId: 'manager', approvalType: 'manager', name: t('time_off.policy.edit.Manager')
      },
      {
        userId: 'managers_manager', approvalType: 'managers_manager', name: t("time_off.policy.edit.Manager's manager")
      },
      ...users.map(u => {
        return { userId: u.id, approvalType: 'user', user: u, name: u.name };
      })
    ];
  }

  get assignee() {
    if (this.typePolicy.secondStageApprovalType === 'manager') {
      return _.find(this.availableAssignees, assignee => assignee.approvalType === 'manager');
    } else if (this.typePolicy.secondStageApprovalType === 'managers_manager') {
      return _.find(this.availableAssignees, assignee => assignee.approvalType === 'managers_manager');
    } else if (this.typePolicy.secondStageApprovalType === 'user') {
      return _.find(this.availableAssignees, assignee => !!this.typePolicy.secondStageApprovalAssigneeUser && assignee.userId === this.typePolicy.secondStageApprovalAssigneeUser.id);
    }

    return null;
  }

  get firstStageAvailableAssignees() {
    return _.filter(this.availableAssignees, assignee => assignee.approvalType !== 'managers_manager');
  }

  async initializeEmailTemplate() {
    await this.store._compose([
      endpoints.COMPANY_EMAIL_TEMPLATES
    ]);

    this.welcomeEmailPreview = this.store._getSingle(types.COMPANY_EMAIL_TEMPLATE, {emailType: 'pto_policy_invitation'});
  }

  updateHoursPerWorkday(value) {
    this.hoursPerWorkday = value;
    this.policy.workDayLength = value * 3600;
    this.workDayLengthChanged = true;
  }

  toggleWorkWeekDay(day) {
    if (this.workdaySelected(day)) {
      _.pull(this.policy.workWeekDays, day);
    } else {
      this.policy.workWeekDays.push(day);
    }
  }

  toggleTimeOffType(type) {
    if (this.timeOffTypeSelected(type)) {
      _.remove(this.selectedTimeOffTypes, t => t.id === type.id);
    } else {
      this.selectedTimeOffTypes.push(type);
    }
  }

  updateSelectedEmployees(employees) {
    this.selectedEmployees.replace(employees);
  }

  openTypeRemovalConfirmationModal() {
    this.typeRemovalConfirmationModalOpen = true;
  }

  closeTypeRemovalConfirmationModal() {
    this.typeRemovalConfirmationModalOpen = false;
  }

  openPolicyConfirmationModal() {
    this.policyConfirmationModalOpen = true;

    this.effectiveDateType = this.effectiveDateType || null;
    this.forecastDate = this.forecastDate || this.forecastDateOptions[0].date;
  }

  closePolicyConfirmationModal() {
    this.policyConfirmationModalOpen = false;
    this.effectiveDateType = null;
  }

  async onStepSubmitted({location}) {
    switch (location) {
      case 'details': return this.submitDetails();
      case 'policy_types': return this.onTimeOffTypesSubmitted();
      case 'employees': return this.employeeStepSubmitted();
      case 'time_off_rules': return this.typeRulesStepSubmitted();
      case 'review': return this.submitReview();
      default:
        throw new Error(`Location ${location} is not supported`);
    }
  }

  async employeeStepSubmitted() {
    if (_.isEmpty(this.newSelectedEmployees)) {
      return this.submitEmployees();
    }
    if (this.policyConfirmationModalOpen) {
      return this.submitEmployees();
    }

    this.openPolicyConfirmationModal();
    this.updateForecastingEmployee(this.newSelectedEmployees[0].id, () => this.forecastPolicy());

    return false;
  }

  async submitDetails() {
    this.workDayLengthChanged = false;

    return this._submitStep(
      ['name', 'workDayLength', 'startMonth', 'startDay', 'workWeekDays'],
      endpoints.TIME_OFF.POLICY.UPDATE_DETAILS.with(this.policy.id)
    );
  }

  async submitReview() {
    const submit = await this._submitStep(
      ['sendEmployeeWelcomeNotifications'],
      endpoints.TIME_OFF.POLICY.COMPLETE.with(this.policy.id)
    );
    if (submit) {
      this.history.push('/policies');
    }
  }

  async onTimeOffTypesSubmitted() {
    if (this.typeRemovalConfirmationModalOpen || this.removedTypes.length === 0) {
      return await this.submitTimeOffTypes();
    } else {
      this.openTypeRemovalConfirmationModal();
      return false;
    }
  }

  async submitTimeOffTypes() {
    this.policy.types = this.selectedTimeOffTypes;

    this.closeTypeRemovalConfirmationModal();

    return this._submitStep(
      ['types'],
      endpoints.TIME_OFF.POLICY.UPDATE_TYPES.with(this.policy.id)
    );
  }

  async submitEmployees() {
    this.policy.employees = this.selectedEmployees;
    this.policy.effectiveDate = this.effectiveDate;

    const submitSuccessful = await this._submitStep(
      ['employees', 'effectiveDate'],
      endpoints.TIME_OFF.POLICY.UPDATE_EMPLOYEES.with(this.policy.id)
    );

    if (submitSuccessful) {
      this.closePolicyConfirmationModal();
      this.forecastingEmployeeId = null;
    }

    return submitSuccessful;
  }

  async typeRulesStepSubmitted() {
    if (this.accrualType === 'unlimited') this.clearTypePolicy();
    if (_.isEmpty(this.policy.employees)) {
      return this.submitTypePolicy();
    }

    const {model, errors} = await this.store.post(
      endpoints.TIME_OFF.TYPE_POLICIES.UPDATE_CONFIRMATION_SUMMARY.with(this.typePolicy.id),
      types.TIME_OFF.TYPE_POLICY_CONFIRMATION_SUMMARY,
      this.transformApprovers(this.typePolicy.pick(this.typePolicyProps))
    );

    if (!model) {
      this.errors = errors;
      return false;
    }
    this.confirmationSummary = new TypePolicyConfirmationSummary(model);

    if (this.confirmationSummary.effectiveDateRequired && !this.policyConfirmationModalOpen) {
      if (this.typePolicy.accrualStart === 'policy_holder_start_date') {
        this.effectiveDateType = 'today';
      }
      this.openPolicyConfirmationModal();
      this.errors = {};
      const employeeId = _.includes(_.map(this.policy.employees, 'id'), this.forecastingEmployeeId) ? this.forecastingEmployeeId : this.policy.employees[0].id;
      this.updateForecastingEmployee(employeeId, () => this.forecastTypePolicy());
      return false;
    }

    const submitSuccessful = this.submitTypePolicy();

    if (submitSuccessful) {
      this.closePolicyConfirmationModal();
      this.confirmationSummary = null;
      this.forecastingEmployeeId = null;
      this.forecastDate = null;
    }

    return submitSuccessful;
  }

  mapApproversForBackend(approvers) {
    return approvers.map(approver => {
      if (approver.approvalType === 'user' || approver.approvalType === 'specific_user') {
        return {
          id: approver.id,
          approvalType: 'specific_user',
          userId: approver.userId
        };
      } else {
        return {
          id: approver.id,
          approvalType: approver.approvalType
        };
      }
    });
  }

  mapApproversFromBackend(approvers) {
    approvers.forEach(approver => {
      if (approver.approvalType === 'specific_user') {
        approver.approvalType = 'user';
        approver.userId = approver.userId.toString();
      } else {
        approver.userId = approver.approvalType;
      }
    });
  }

  transformApprovers(typePolicy) {
    if (this.typePolicy.approvalType === 'auto') {
      typePolicy.firstStageApprovers = [];
    } else {
      typePolicy.firstStageApprovers = this.mapApproversForBackend(typePolicy.firstStageApprovers);
    }

    if (this.typePolicy.approvalType === 'two_stage') {
      typePolicy.secondStageApprovers = this.mapApproversForBackend(typePolicy.secondStageApprovers);
    } else {
      typePolicy.secondStageApprovers = [];
    }

    return typePolicy;
  }

  async submitTypePolicy() {
    const typePolicy = this.typePolicy.pick(this.typePolicyProps);

    this.transformApprovers(typePolicy);

    if (this.effectiveDate) {
      typePolicy.newEffectiveDate = this.effectiveDate;
    }
    const {model, errors} = await this.store.patch(typePolicy);

    this.errors = errors;
    if (!model) return false;

    this.typePolicy.update(model);
    return true;
  }

  async _submitStep(props, endpoint) {
    const policy = this.policy.pick(props);
    const {model, errors} = await this.store.patch(endpoint, types.TIME_OFF.POLICY, policy);

    this.errors = errors;
    if (!model) return false;

    this.policy.update(model);
    this._initializeSteps();

    if (!this.steps.nextStep()) return true;

    return {
      nextLocation: `${this.steps.path(this.steps.nextStep())}`
    };
  }

  selectAssignee(assignee) {
    this.typePolicy.secondStageApprovalType = assignee.approvalType;
    this.typePolicy.secondStageApprovalAssigneeUser = assignee.user;
  }

  addFirstStageApprover(approver) {
    this.typePolicy.firstStageApprovers.push(approver);
  }

  removeFirstStageApprover(approver) {
    _.remove(this.typePolicy.firstStageApprovers, a => a.userId === approver.userId);
  }

  addSecondStageApprover(approver) {
    this.typePolicy.secondStageApprovers.push(approver);
  }

  removeSecondStageApprover(approver) {
    _.remove(this.typePolicy.secondStageApprovers, a => a.userId === approver.userId);
  }

  selectAccrualType(accrualType) {
    switch(accrualType) {
      case 'standard':
        this.accrualType = accrualType;
        if (this.typePolicy.accrualFrequency === 'unlimited') this.resetTypePolicy();
        break;
      case 'unlimited':
        this.accrualType = accrualType;
        this.typePolicy.preventNegativeBalance = false;
        break;
      default:
        throw new Error(`Accrual type ${accrualType} is not supported`);
    }
  }

  clearTypePolicy() {
    this.typePolicy.accrualFrequency = 'unlimited';
    this.typePolicy.baseAccrualRate = null;
    this.typePolicy.accrualStartDate = null;
    this.typePolicy.accrueUpfront = true;
    this.typePolicy.balanceAdjustmentPolicy = null;
    this.typePolicy.anniversaryBonuses = [];
  }

  resetTypePolicy() {
    this.typePolicy.accrualFrequency = 'annual';
    this.initializeBalanceAdjustmentPolicy();
    this.typePolicy.baseAccrualRate = '';
    this.typePolicy.accrualStartDate = moment().toDate();
    this.typePolicy.anniversaryBonuses = [];
  }

  selectCarryoverType(carryoverType) {
    // TODO: Ensure we throw an error if you set capped with deadline but don't set a limit
    switch(carryoverType) {
      case 'annual_reset':
        this.typePolicy.balanceAdjustmentPolicy = new BalanceAdjustmentPolicy({type: 'AnnualResetPolicy'});
        break;
      case 'no_limit':
        this.typePolicy.balanceAdjustmentPolicy = new BalanceAdjustmentPolicy({type: 'NoLimitPolicy'});
        break;
      case 'balance_limit':
        this.typePolicy.balanceAdjustmentPolicy = new BalanceAdjustmentPolicy({type: 'BalanceLimitPolicy', parameter: ''});
        break;
      case 'capped':
        this.typePolicy.balanceAdjustmentPolicy = new BalanceAdjustmentPolicy({type: 'CarryoverPolicy', parameter: ''});
        break;
      case 'capped_with_deadline':
        this.typePolicy.balanceAdjustmentPolicy = new BalanceAdjustmentPolicy({type: 'CarryoverPolicy', parameter: '', limitDays: ''});
        break;
      default:
        throw new Error(`Carryover type ${carryoverType} is not supported`);
    }
    this.carryoverType = carryoverType;
  }

  toggleWaitingPeriod(waitPeriodEnabled) {
    this.waitPeriodEnabled = waitPeriodEnabled;
    this.typePolicy.waitPeriod = waitPeriodEnabled ? '' : null;
  }

  addAnniversaryBonus() {
    const newBonus = new AnniversaryBonus({anniversary: '', effectiveRate: ''});
    this.typePolicy.anniversaryBonuses.push(newBonus);
  }

  removeAnniversaryBonus(anniversaryBonus) {
    this.typePolicy.anniversaryBonuses.remove(anniversaryBonus);
  }

  async updateEffectiveDateType(effectiveDateType, updateForecast) {
    this.effectiveDateType = effectiveDateType;
    await updateForecast();
  }

  updateAccrualStart(accrualStart) {
    this.typePolicy.accrualStart = accrualStart;
  }

  async updateForecastDate(forecastDate, updateForecast) {
    this.forecastDate = forecastDate;
    await updateForecast();
  }

  async updateForecastingEmployee(employeeId, updateForecast) {
    this.forecastingEmployeeId = employeeId;
    await updateForecast();
  }

  async forecastPolicy() {
    if (!this.forecastingEmployeeId || !this.effectiveDate || !this.forecastDate) return;

    this.isForecastingEmployee = true;

    const {model} = await this.store.post(
      endpoints.TIME_OFF.FORECAST_UPDATE_POLICY.with(this.forecastingEmployeeId),
      types.TIME_OFF.POLICY_CHANGE_FORECAST,
      {
        effectiveDate: moment(this.effectiveDate).format('YYYY-MM-DD'),
        forecastDate: moment(this.forecastDate).format('YYYY-MM-DD'),
        timeOffPolicy: this.policy
      }
    );

    this.forecastedValues = model;
    this.isForecastingEmployee = false;
  }

  async forecastTypePolicy() {
    if (!this.forecastingEmployeeId || !this.effectiveDate || !this.forecastDate) return;

    this.isForecastingEmployee = true;

    const {model} = await this.store.post(
      endpoints.TIME_OFF.TYPE_POLICIES.FORECAST_UPDATE.with({
        id: this.typePolicy.id,
        effectiveDate: this.effectiveDate,
        forecastDate: this.forecastDate,
        employeeId: this.forecastingEmployeeId
      }),
      types.TIME_OFF.POLICY_CHANGE_FORECAST,
      this.transformApprovers(this.typePolicy).pick(this.typePolicyProps)
    );

    this.forecastedValues = model;
    this.isForecastingEmployee = false;
  }

  get sortedTypes() {
    if (!this.timeOffTypes || !this.forecastedValues) return [];

    return _.chain(this.timeOffTypes)
      .filter(type => _.includes(Object.keys(this.forecastedValues.beforeValues), type.id))
      .sortBy('order')
      .value();
  }

  get predictedAccountRows() {
    if (!this.forecastedValues) return [];

    return this.sortedTypes.map(type => ({
      id: type.id,
      name: type.name,
      oldValue: this.balanceView(this.forecastedValues.beforeValues[type.id], type.unit),
      newValue: this.balanceView(this.forecastedValues.afterValues[type.id], type.unit)
    }));
  }

  get willApprovePendingRequests() {
    return (this.oldTypePolicy.approvalType === 'single' || this.oldTypePolicy.approvalType === 'two_stage') && this.typePolicy.approvalType === 'auto';
  }

  get willModifySecondStageRequests() {
    return (this.oldTypePolicy.approvalType === 'two_stage' && this.typePolicy.approvalType === 'two_stage') && (this.typePolicy.secondStageApprovalType !== this.oldTypePolicy.secondStageApprovalType || _.get(this.typePolicy.secondStageApprovalAssigneeUser, 'id') !== _.get(this.oldTypePolicy.secondStageApprovalAssigneeUser, 'id'));
  }

  get willApproveSecondStageRequests() {
    return this.oldTypePolicy.approvalType === 'two_stage' && this.typePolicy.approvalType === 'single';
  }

  get displayApprovalChangeWarning() {
    return !_.isEmpty(this.policy.employees) && (this.willApprovePendingRequests || this.willApproveSecondStageRequests || this.willModifySecondStageRequests);
  }

  get removedTypes() {
    return _.differenceBy(this.currentlySelectedTimeOffTypes, this.selectedTimeOffTypes, 'id');
  }

  get predictedAccountColumns() {
    if (this.forecastedValues && this.forecastedValues.oldPolicyName !== this.policy.name) {
      return [
        {
          header: t('time_off.policy.edit.Time Off Type'),
          width: 4,
          attribute: 'name'
        },
        {
          attribute: 'oldValue',
          header: t('time_off.policy.edit.FORECAST_BEFORE_HEADER', { policy: this.forecastedValues.oldPolicyName || t('time_off.policy.edit.No Policy') }),
          width: 4
        },
        {
          attribute: 'newValue',
          header: t('time_off.policy.edit.FORECAST_AFTER_HEADER', { policy: this.policy.name }),
          width: 4
        }
      ];
    }

    return [
      {
        header: 'time_off.policy.edit.Time Off Type',
        width: 4,
        attribute: 'name'
      },
      {
        attribute: 'oldValue',
        header: t('time_off.policy.edit.Before'),
        width: 4
      },
      {
        attribute: 'newValue',
        header: t('time_off.policy.edit.After'),
        width: 4
      }
    ];
  }

  get forecastingEmployee() {
    return _.find(this.selectedEmployees, {id: this.forecastingEmployeeId});
  }
  get daysForStartMonth() {
    return new Date(new Date().getFullYear(), this.policy.startMonth, 0).getDate();
  }

  get sortedTimeOffTypes() {
    return _.orderBy(this.timeOffTypes, 'order');
  }

  get forecastDateOptions() {
    return [
      {
        label: 'time_off.policy.edit.TODAY',
        date: moment().format('YYYY-MM-DD'),
        dateView: calendarDate(moment())
      },
      {
        label: 'time_off.policy.edit.END_OF_POLICY_YEAR',
        date: this.policy.currentYearEndDate,
        dateView: this.policy.currentYearEndDateView
      },
      {
        label: 'time_off.policy.edit.START_OF_NEXT_POLICY_YEAR',
        date: this.policy.nextYearStartDate,
        dateView: this.policy.nextYearStartDateView
      },
    ];
  }

  get effectiveDateOptions() {
    return [
      {
        label: 'time_off.policy.edit.START_OF_POLICY_YEAR',
        type: 'startOfPolicyYear',
        dateView: this.policy.currentYearStartDateView
      },
      {
        label: 'time_off.policy.edit.TODAY',
        type: 'today',
        dateView: calendarDate(this.today)
      }
    ];
  }

  get effectiveDate() {
    if (this.effectiveDateType === 'startOfPolicyYear') {
      return this.policy.currentYearStartDate;
    } else if (this.effectiveDateType === 'today') {
      return this.today;
    } else {
      return null;
    }
  }

  get today() {
    return moment().format('YYYY-MM-DD');
  }

  get newSelectedEmployees() {
    let newSelectedEmployees = _.filter(this.selectedEmployees, employee => !_.find(this.policy.employees, {id: employee.id}));

    if (!_.isEmpty(this.forecastingEmployee)) {
      newSelectedEmployees = _.union(newSelectedEmployees, [this.forecastingEmployee]);
    }

    return newSelectedEmployees;
  }

  get usesAccrualStartDate() {
    return this.accrualType === 'standard' && (this.typePolicy.accrualFrequency === 'weekly' || this.typePolicy.accrualFrequency === 'biweekly');
  }

  _initializeSteps() {
    const steps = _.clone(initialSteps);

    if (!_.isEmpty(this.policy.typePolicies)) {
      steps.push(...this.policy.sortedTypePolicies.map(t => {
        return {
          name: t.type.name,
          translateName: false,
          subStepLocation: t.id,
          useCustomLayout: true,
          location: 'time_off_rules'
        };
      }));
    }

    steps.push(...finalSteps);
    const stepsObject = new Steps(steps.map((s, index) => ({ ...s, index: index + 1 })), {enableStepSkipping: true});

    if (this.steps && this.steps.activeStep) {
      stepsObject.setActiveStep(this.steps.activeStep);
    }

    this.steps = stepsObject;

    if (this.policy.completed) {
      this.steps.enableNavigationToAllSteps();
    }
  }

  balanceView(balance, unit) {
    if (balance === null) return t('time_off.policy.edit.N/A');
    if (balance === 'unlimited') return t('time_off.policy.edit.Flexible');

    return formatTimeOffAmount(balance, unit);
  }

  workdaySelected(day) {
    return this.policy.workWeekDays.includes(day);
  }

  timeOffTypeSelected(type) {
    return _.find(this.selectedTimeOffTypes, { id: type.id });
  }
}

export default PolicyEditFlowState;
